import {
  enumerateDays,
  socialNetworkTypeIdToChartKey,
} from "./messagesReport"

const HOURS_PER_DAY = 24

/** Column order for weekly heatmap (Sun-first, matches MessageHeatmapUnifiedWidget). */
const WEEKLY_DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]

const WEEKLY_PERIOD_TO_COL = Object.fromEntries(
  WEEKLY_DAY_NAMES.map((name, index) => [name.toLowerCase(), index]),
)

/**
 * Messages heatmap rows may omit viewType. Weekday periods are weekly,
 * YYYY-MM-DD periods are daily, and YYYY-MM periods are monthly.
 */
function heatmapRowViewType(row) {
  const explicit = String(row?.viewType ?? "").trim().toLowerCase()
  if (explicit) return explicit

  const period = String(row?.period ?? "").trim()
  if (!period) return ""
  if (WEEKLY_PERIOD_TO_COL[period.toLowerCase()] != null) return "weekly"
  if (/^\d{4}-\d{2}-\d{2}/.test(period)) return "daily"
  if (/^\d{4}-\d{2}$/.test(period)) return "monthly"
  return ""
}

/**
 * Map API weekly period (e.g. "Monday") to matrix column index (Sun=0 … Sat=6).
 * @param {string | number} period
 * @returns {number | null}
 */
export function weeklyPeriodToColumnIndex(period) {
  const raw = String(period ?? "").trim()
  if (!raw) return null

  const byName = WEEKLY_PERIOD_TO_COL[raw.toLowerCase()]
  if (byName != null) return byName

  const numeric = Number(raw)
  if (!Number.isNaN(numeric) && numeric >= 0 && numeric <= 6) return numeric

  return null
}

function hourLabel12(h) {
  const x = h % 12
  const suffix = h < 12 ? "AM" : "PM"
  const num = x === 0 ? 12 : x
  return `${num}${suffix}`
}

const HOUR_LABELS_24 = Array.from({ length: HOURS_PER_DAY }, (_, h) => hourLabel12(h))

function trimMatrixToActiveHourRows(matrix, hourLabels) {
  if (!matrix.length || !hourLabels.length) return { matrix: [], hours: [] }
  let first = 0
  let last = matrix.length - 1
  while (first <= last && matrix[first].every((v) => Number(v) === 0)) first += 1
  while (last >= first && matrix[last].every((v) => Number(v) === 0)) last -= 1
  if (first > last) {
    return { matrix, hours: hourLabels }
  }
  return {
    matrix: matrix.slice(first, last + 1),
    hours: hourLabels.slice(first, last + 1),
  }
}

function shouldIncludeRow(row, socialNetworkTypes, platformKeysFilter) {
  const platformKey = socialNetworkTypeIdToChartKey(
    socialNetworkTypes,
    row?.socialNetworkTypeId,
  )
  if (!platformKey) return false
  if (platformKeysFilter?.length && !platformKeysFilter.includes(platformKey)) {
    return false
  }
  return true
}

/**
 * Sum count by period + hour for a single viewType.
 * @returns {Map<string, number>} keys `${period}|${hour}`
 */
function aggregateByPeriodHour(rows, viewType) {
  const map = new Map()
  for (const row of rows) {
    if (heatmapRowViewType(row) !== viewType) continue
    const period = String(row?.period ?? "")
    const hour = Number(row?.hour)
    if (!period || Number.isNaN(hour) || hour < 0 || hour > 23) continue
    const key = `${period}|${hour}`
    const count = heatmapRowPostCount(row)
    map.set(key, (map.get(key) || 0) + count)
  }
  return map
}

function heatmapRowPostCount(row) {
  const n = Number(row?.count ?? row?.messageCount ?? row?.postCount)
  return Number.isFinite(n) ? n : 0
}

function periodToMonthKey(period) {
  const raw = String(period ?? "").trim()
  if (!raw) return null
  if (/^\d{4}-\d{2}$/.test(raw)) return raw
  const iso = raw.slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso.slice(0, 7)
  return null
}

function buildDailyHeatmap(filteredRows, startDate, endDate) {
  const calendarDates = enumerateDays(startDate, endDate)
  const n = calendarDates.length
  if (n === 0) {
    return { calendarDates: [], hourlyDailyMatrix: { matrix: [], hours: [] } }
  }

  const agg = aggregateByPeriodHour(filteredRows, "daily")
  const matrix = Array.from({ length: HOURS_PER_DAY }, () => Array(n).fill(0))

  for (let d = 0; d < n; d += 1) {
    const iso = calendarDates[d]
    for (let h = 0; h < HOURS_PER_DAY; h += 1) {
      matrix[h][d] = agg.get(`${iso}|${h}`) || 0
    }
  }

  const trimmed = trimMatrixToActiveHourRows(matrix, HOUR_LABELS_24)
  return { calendarDates, hourlyDailyMatrix: trimmed }
}

function buildWeeklyHeatmap(filteredRows) {
  const agg = aggregateByPeriodHour(filteredRows, "weekly")
  const matrix = Array.from({ length: HOURS_PER_DAY }, () => Array(7).fill(0))

  for (const [key, count] of agg.entries()) {
    const [period, hourStr] = key.split("|")
    const dayIndex = weeklyPeriodToColumnIndex(period)
    const hour = Number(hourStr)
    if (dayIndex == null) continue
    if (Number.isNaN(hour) || hour < 0 || hour > 23) continue
    matrix[hour][dayIndex] += count
  }

  const trimmed = trimMatrixToActiveHourRows(matrix, HOUR_LABELS_24)
  return {
    weeklyHours: trimmed.hours,
    weeklyDays: WEEKLY_DAY_NAMES,
    weeklyValues: trimmed.matrix,
  }
}

function buildMonthlyHeatmap(filteredRows) {
  const agg = aggregateByPeriodHour(filteredRows, "monthly")
  const monthTotals = new Map()

  for (const [key, count] of agg.entries()) {
    const [period] = key.split("|")
    const ym = periodToMonthKey(period)
    if (!ym) continue
    monthTotals.set(ym, (monthTotals.get(ym) || 0) + count)
  }

  const monthKeys = [...monthTotals.keys()].sort()
  if (monthKeys.length === 0) {
    return { monthlyHourlyBundle: { matrix: [], hours: [], dates: [] } }
  }

  const matrix = Array.from({ length: HOURS_PER_DAY }, () =>
    Array(monthKeys.length).fill(0),
  )

  for (const [key, count] of agg.entries()) {
    const [period, hourStr] = key.split("|")
    const ym = periodToMonthKey(period)
    if (!ym) continue
    const colIndex = monthKeys.indexOf(ym)
    if (colIndex < 0) continue
    const hour = Number(hourStr)
    if (Number.isNaN(hour) || hour < 0 || hour > 23) continue
    matrix[hour][colIndex] += count
  }

  const trimmed = trimMatrixToActiveHourRows(matrix, HOUR_LABELS_24)
  return {
    monthlyHourlyBundle: {
      ...trimmed,
      dates: monthKeys,
    },
  }
}

/**
 * Transform GET …/heatmap rows into MessageHeatmapUnifiedWidget props.
 *
 * @param {Array<{
 *   viewType?: string,
 *   count?: number,
 *   period?: string,
 *   socialNetworkTypeId?: number,
 *   hour?: number,
 * }>} rows
 * @param {Array<{ id?: number, description?: string, name?: string }>} socialNetworkTypes
 * @param {{
 *   startDate?: Date,
 *   endDate?: Date,
 *   platformKeysFilter?: string[] | null,
 * }} [options]
 */
export function buildMessageHeatmapFromRows(
  rows,
  socialNetworkTypes = [],
  options = {},
) {
  const safeRows = Array.isArray(rows) ? rows : []
  const { startDate, endDate, platformKeysFilter } = options

  const filtered = safeRows.filter((row) =>
    shouldIncludeRow(row, socialNetworkTypes, platformKeysFilter),
  )

  const daily = buildDailyHeatmap(filtered, startDate, endDate)
  const weekly = buildWeeklyHeatmap(filtered)
  const monthly = buildMonthlyHeatmap(filtered)

  return {
    calendarDates: daily.calendarDates,
    hourlyDailyMatrix: daily.hourlyDailyMatrix,
    weeklyHours: weekly.weeklyHours,
    weeklyDays: weekly.weeklyDays,
    weeklyValues: weekly.weeklyValues,
    monthlyHourlyBundle: monthly.monthlyHourlyBundle,
  }
}
